import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { CitationCardComponent } from '../../shared/components/citation-card/citation-card.component';
import { TopPromptByPlatformItem, PLATFORM_LABELS } from '../../shared/models/top-prompt-by-platform.model';

export interface TopPromptByPlatformDialogData {
  item: TopPromptByPlatformItem;
}

@Component({
  selector: 'app-top-prompt-by-platform-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatAngularModule,
    CitationCardComponent
  ],
  templateUrl: './top-prompt-by-platform-dialog.component.html',
  styleUrl: './top-prompt-by-platform-dialog.component.scss'
})
export class TopPromptByPlatformDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<TopPromptByPlatformDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TopPromptByPlatformDialogData
  ) {}

  get item(): TopPromptByPlatformItem {
    return this.data?.item ?? {} as TopPromptByPlatformItem;
  }

  getPlatformLabel(platform: string): string {
    return PLATFORM_LABELS[platform] ?? platform;
  }

  /** Domain favicon URL (same as AI-citations Mentions column). */
  getFaviconUrl(domain: string): string {
    if (!domain || typeof domain !== 'string') return '';
    const d = domain.trim().replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '');
    return d ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(d)}&sz=32` : '';
  }

  /** First letter(s) for brand chip (same as AI-citations). */
  getBrandInitial(name: string): string {
    if (!name || typeof name !== 'string') return '?';
    const t = name.trim();
    if (!t) return '?';
    const parts = t.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
    return t[0].toUpperCase();
  }

  onFaviconError(event: Event): void {
    const el = event.target as HTMLImageElement;
    if (el) el.style.display = 'none';
  }

  formatResponse(text: string): string {
    if (!text) return '';
    return text.split(/\n\n/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
  }

  formatCitations(citations: any[]): any[] {
    if (!Array.isArray(citations) || citations.length === 0) return [];
    return citations.map((c, i) => {
      const url = typeof c === 'string' ? c : (c?.url ?? c?.link ?? '');
      let domain = '';
      try {
        domain = url ? new URL(url).hostname : '';
      } catch {
        const m = (url || '').match(/https?:\/\/([^/]+)/);
        domain = m ? m[1] : '';
      }
      return {
        id: i + 1,
        title: (typeof c === 'object' && c?.title) ? c.title : url,
        url,
        snippet: '',
        domain,
        confidence: 1.0
      };
    }).filter(c => c.url);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
